/**
 * Test if we have localstorage enabled
 * @returns boolean
 */
const isLocalStorageEnabled = () => {
    try {
        // First check if localStorage object is accessible
        // In some environments (iframes, private browsing), accessing window.localStorage
        // itself can throw a SecurityError, so we need to check for existence first
        if (typeof window === "undefined" || !window) {
            return false;
        }

        // Assign to variable to ensure property access is wrapped in try-catch
        const storage = window.localStorage;
        if (!storage) {
            return false;
        }

        const key = `_storage_test`;
        storage.setItem(key, "yes");
        storage.removeItem(key);
        return true;
    } catch (e) {
        // SecurityError: Failed to read the 'localStorage' property from 'Window'
        // or QuotaExceededError, etc. All mean localStorage is not available
        return false;
    }
};

/**
 *
 * @param name name of the localstorage
 * @returns string
 */

const defaultExpiry = 60 * 60; // one hour
const expiresKey = "_expiresIn";

export const getLocalStorage = (name: string, skipExpiryCheck = false) => {
    if (!isLocalStorageEnabled()) {
        console.log(
            "Localstorage is not available. Please check if the browser supports localstorage."
        );
        return null;
    }

    const now = Date.now();
    const expiresIn = parseInt(localStorage.getItem(name + expiresKey) || "0");

    if (expiresIn < now && !skipExpiryCheck) {
        removeLocalStorage(name);
        return null;
    } else {
        try {
            const value = localStorage.getItem(name);
            return value;
        } catch (e) {
            console.log(
                `Error reading ${name} from localstorage ${JSON.stringify(e)}`
            );
            return null;
        }
    }
};

/**
 * setLocalStorage : create a local storage with name and value, and set the expiry.
 * @param name localstorage name
 * @param value localstorage value
 * @param expires ttl in seconds for the localstorage
 * @returns boolean
 */
export const setLocalStorage = (
    name: string,
    value: string,
    expires = defaultExpiry
) => {
    if (!isLocalStorageEnabled()) {
        console.log(
            "Localstorage is not available. Please check if the browser supports localstorage."
        );
        return false;
    }

    expires = Math.abs(expires);

    const now = Date.now();
    const schedule = now + expires * 1000;
    try {
        localStorage.setItem(name, value);
        localStorage.setItem(name + expiresKey, schedule.toString());
    } catch (e) {
        console.log(
            `Error setting ${name} in localstorage ${JSON.stringify(e)}`
        );
        return false;
    }
    return true;
};

/**
 *
 * @param name localstorage name
 * @returns boolean
 */
export const removeLocalStorage = (name: string) => {
    if (!isLocalStorageEnabled()) {
        console.log(
            "Localstorage is not available. Please check if the browser supports localstorage."
        );
        return false;
    }

    try {
        localStorage.removeItem(name);
        localStorage.removeItem(name + expiresKey);
    } catch (e) {
        console.log(
            `Error removing ${name} in localstorage ${JSON.stringify(e)}`
        );
        return false;
    }
    return true;
};
